import React, { useState, useEffect } from "react";
import { AutoTooltip } from "utils/Constant";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider,
  Grid,
  Tabs,
  Tab,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EvStationIcon from "@mui/icons-material/EvStation";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { useSnackbar } from "notistack";
import useApiRequest from "utils/Apirequest";
import { getDecodedUserData } from "utils/Constant";
import Loader from "./Loader";
import LocationOperationalHoursModal from "components/LocationOperationalHoursModal";
import ChargerOperationalTimingsModal from "components/ChargerOperationalTimingsModal";
import NotificationIntervalSettings from "components/NotificationIntervalSettings";
import NotificationRecipientSettings from "components/NotificationRecipientSettings";

const SettingsModal = ({ open, onClose }) => {
  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;
  const { enqueueSnackbar } = useSnackbar();

  const [tabValue, setTabValue] = useState(0);
  const [radius, setRadius] = useState("");
  const [waitTime, setWaitTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [chargerTimingsModalOpen, setChargerTimingsModalOpen] = useState(false);
  const [chargerLocations, setChargerLocations] = useState([]);
  const [chargerLocationsLoading, setChargerLocationsLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const userData = getDecodedUserData();

  const apiRequest = useApiRequest();

  useEffect(() => {
    if (!open) return;

    fetchSettings();

    if (tabValue === 1) fetchLocations();
    if (tabValue === 2) fetchChargerLocations();
    if (tabValue === 3) fetchRecipients();

  }, [open, tabValue]);

  const fetchSettings = async () => {
    setLoading(true);
    const { data } =
      (await apiRequest(
        "get",
        "spot-booking/config/radius",
        null,
        {},
        {},
        false
      )) || {};
    if (data?.success) {
      setRadius(data?.data?.currentRadius);
    }

    const { data: waitData } =
      (await apiRequest(
        "get",
        "spot-booking/config/wait-time",
        null,
        {},
        {},
        false
      )) || {};
    if (waitData?.success) {
      setWaitTime(waitData?.data?.currentWaitTime);
    }

    setLoading(false);
  };
  const [errors, setErrors] = useState({
    radius: "",
    waitTime: "",
  });
  const validateField = (name, value) => {
    if (value === "" || value === null) {
      return "Value is required";
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return "Invalid number";
    }

    if (numericValue <= 0) {
      return "Value must be greater than zero";
    }

    return "";
  };
  const handleRadiusChange = (e) => {
    const value = e.target.value;
    setRadius(value);
    setErrors((prev) => ({
      ...prev,
      radius: validateField("radius", value),
    }));
  };

  const handleWaitTimeChange = (e) => {
    const value = e.target.value;
    setWaitTime(value);
    setErrors((prev) => ({
      ...prev,
      waitTime: validateField("waitTime", value),
    }));
  };

  const handleSubmit = async () => {
    //  Final validation guard (UI + backend safety)
    const radiusError = validateField("radius", radius);
    const waitTimeError = validateField("waitTime", waitTime);

    if (radiusError || waitTimeError) {
      setErrors({
        radius: radiusError,
        waitTime: waitTimeError,
      });

      enqueueSnackbar("Please fix validation errors before saving", {
        variant: "error",
      });
      return;
    }

    setSubmitting(true);

    try {
      //  Radius update
      const radiusPayload = {
        radius: parseFloat(radius),
        updatedBy: userData?.userID,
        userRole: userData?.role,
        userName: userData?.username,
      };

      const { data: radiusData } =
        (await apiRequest(
          "post",
          "spot-booking/config/radius",
          radiusPayload,
          {},
          {},
          false
        )) || {};

      if (radiusData?.success) {
        enqueueSnackbar("Radius updated successfully", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(radiusData?.message || "Failed to update radius", {
          variant: "error",
        });
      }

      //  Wait Time update
      const waitTimePayload = {
        waitTime: parseFloat(waitTime),
        updatedBy: userData?.userID,
        userRole: userData?.role,
        userName: userData?.username,
      };

      const { data: waitData } =
        (await apiRequest(
          "post",
          "spot-booking/config/wait-time",
          waitTimePayload,
          {},
          {},
          false
        )) || {};

      if (waitData?.success) {
        enqueueSnackbar("Wait Time updated successfully", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(waitData?.message || "Failed to update Wait Time", {
          variant: "error",
        });
      }

      //  Close modal only after successful flow
      onClose();
    } catch (error) {
      console.error("Error saving spot booking config:", error);
      enqueueSnackbar("Something went wrong. Please try again.", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fetchLocations = async () => {
    try {
      setLocationsLoading(true);
      const { data } = await apiRequest(
        "get",
        "location-operational-hours/all",
        null,
        {},
        {}
      );
      if (data?.success) {
        console.log("Fetched locations data:", data.data);
        setLocations(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLocationsLoading(false);
    }
  };

  const handleLocationConfig = (locationId) => {
    console.log(
      "SettingsModal: Opening location config for locationId:",
      locationId
    );
    if (!locationId) {
      console.error("SettingsModal: locationId is undefined!");
      enqueueSnackbar("Invalid location selected", { variant: "error" });
      return;
    }
    setSelectedLocationId(locationId);
    setLocationModalOpen(true);
  };

  const handleLocationSave = () => {
    setLocationModalOpen(false);
    setSelectedLocationId(null);
    fetchLocations();
  };

  const fetchChargerLocations = async () => {
    try {
      setChargerLocationsLoading(true);
      const { data } = await apiRequest(
        "get",
        "charger-operational-timings/all",
        null,
        {},
        {}
      );
      if (data?.success) {
        setChargerLocations(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching charger locations:", error);
    } finally {
      setChargerLocationsLoading(false);
    }
  };

  const handleChargerTimingsConfig = (locationId) => {
    setSelectedLocationId(locationId);
    setChargerTimingsModalOpen(true);
  };

  const handleChargerTimingsSave = () => {
    fetchChargerLocations();
  };

  const fetchRecipients = async () => {
    try {
      setRecipientsLoading(true);
      const { data } = await apiRequest("get", "notification-config", null, {}, {});
      if (data?.success) {
        setRecipients(Array.isArray(data.data) ? data.data : []);
      }
    } catch (error) {
      console.error("Error fetching recipients:", error);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const handleAddRecipient = async (recipientData) => {
    try {
      const { data } = await apiRequest("post", "notification-config", recipientData, {}, {});
      if (data?.success) {
        fetchRecipients();
        return { success: true };
      } else {
        return { success: false, message: data?.message || 'Failed to add recipient' };
      }
    } catch (error) {
      console.error("Error adding recipient:", error);
      return { success: false, message: error.message };
    }
  };
  const isFormInvalid =
    Boolean(errors.radius) || Boolean(errors.waitTime) || !radius || !waitTime;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"lg"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: darkMode ? "#1a2035" : "#fff",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          pt: 3,
          backgroundColor: darkMode ? "#202940" : "#fff",
        }}
      >
        <MDTypography variant="h4" fontWeight="bold">
          Settings
        </MDTypography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <MDBox px={3} py={1}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: '64px',
              fontSize: '0.875rem',
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <Tab
            icon={<SettingsIcon />}
            iconPosition="start"
            label="Spot Booking"
          />
          <Tab
            icon={<LocationOnIcon />}
            iconPosition="start"
            label="Location Hours"
          />
          <Tab
            icon={<EvStationIcon />}
            iconPosition="start"
            label="Charger Timings"
          />
          {/* <Tab
            icon={<NotificationsActiveIcon />}
            iconPosition="start"
            label="Notification Intervals"
          /> */}
          <Tab
            icon={<NotificationsActiveIcon />}
            iconPosition="start"
            label="Notification Recipients"
          />
        </Tabs>
      </MDBox>

      <Divider sx={{ mx: 3 }} />

      <DialogContent
        sx={{ px: 3, py: 2, backgroundColor: darkMode ? "#1a2035" : "#fff" }}
      >
        {tabValue === 0 &&
          (loading ? (
            <MDBox textAlign="center">
              <Loader />
            </MDBox>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ mt: 1 }}>
                <MDInput
                  fullWidth
                  type="number"
                  label="Radius (m)"
                  value={radius}
                  onChange={handleRadiusChange}
                  error={Boolean(errors.radius)}
                  helperText={errors.radius}
                  inputProps={{
                    min: 0.1,
                    step: "0.01",
                  }} />
              </Grid>

              <Grid item xs={12} sx={{ mt: 1 }}>
                <MDInput
                  fullWidth
                  type="number"
                  label="Wait Time (minutes)"
                  value={waitTime}
                  onChange={handleWaitTimeChange}
                  error={Boolean(errors.waitTime)}
                  helperText={errors.waitTime}
                  inputProps={{
                    min: 0.1,
                    step: "0.01",
                  }} />
              </Grid>

              <Grid item xs={12} sx={{ mt: 3 }}>
                <MDButton
                  fullWidth
                  variant="gradient"
                  color={sidenavColor}
                  onClick={handleSubmit}
                  disabled={submitting || isFormInvalid}
                >
                  {submitting ? "Saving..." : "Submit"}
                </MDButton>
              </Grid>
            </Grid>
          ))}

        {tabValue === 1 && (
          <MDBox mt={2}>
            {locationsLoading ? (
              <MDBox textAlign="center" py={3}>
                <Loader />
              </MDBox>
            ) : locations.length === 0 ? (
              <MDBox textAlign="center" py={3}>
                <MDTypography variant="body2" color="text">
                  No locations found
                </MDTypography>
              </MDBox>
            ) : (
              <Card
                sx={{
                  backgroundColor: darkMode ? "#202940" : "#f8f9fa",
                  border: `1px solid ${darkMode ? "#344767" : "#e9ecef"}`,
                  boxShadow: "none",
                }}
              >
                <CardContent>
                  <TableContainer sx={{ overflowX: "hidden" }}>
                    <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "20%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Location Name
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ width: "25%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Address
                            </MDTypography>
                          </TableCell>
                          <TableCell align="center" sx={{ width: "15%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Status
                            </MDTypography>
                          </TableCell>
                          <TableCell align="center" sx={{ width: "15%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Working Hours
                            </MDTypography>
                          </TableCell>
                          <TableCell align="center" sx={{ width: "15%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Action
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {locations.map((location) => (
                          <TableRow key={location.locationId || location._id}>
                            <TableCell sx={{ width: "25%" }}>
                              <AutoTooltip maxWidth={150}>
                                {location.locationName}
                              </AutoTooltip>
                            </TableCell>

                            <TableCell sx={{ width: "25%" }}>
                              <AutoTooltip>
                                {location.address || "N/A"}

                              </AutoTooltip>
                            </TableCell>

                            <TableCell align="center" sx={{ width: "15%" }}>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                color={
                                  location.operationalHours?.enabled
                                    ? "green"
                                    : "error"
                                }
                              >
                                {location.operationalHours?.enabled
                                  ? "ENABLED"
                                  : "DISABLED"}
                              </MDTypography>
                            </TableCell>
                            <TableCell align="center" sx={{ width: "15%" }}>
                              <MDTypography
                                variant="caption"
                                color={darkMode ? "white" : "dark"}
                              >
                                {location.operationalHours?.enabled
                                  ? `${location.operationalHours.startTime} - ${location.operationalHours.endTime}`
                                  : "N/A"}
                              </MDTypography>
                            </TableCell>
                            <TableCell align="center" sx={{ width: "15%" }}>
                              <MDButton
                                variant="gradient"
                                color={sidenavColor}
                                size="small"
                                onClick={() =>
                                  handleLocationConfig(
                                    location.locationId || location._id
                                  )
                                }
                              >
                                Configure
                              </MDButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </MDBox>
        )}

        {tabValue === 2 && (
          <MDBox mt={2}>
            {chargerLocationsLoading ? (
              <MDBox textAlign="center" py={3}>
                <Loader />
              </MDBox>
            ) : chargerLocations.length === 0 ? (
              <MDBox textAlign="center" py={3}>
                <MDTypography variant="body2" color="text">
                  No locations found
                </MDTypography>
              </MDBox>
            ) : (
              <Card
                sx={{
                  backgroundColor: darkMode ? "#202940" : "#f8f9fa",
                  border: `1px solid ${darkMode ? "#344767" : "#e9ecef"}`,
                  boxShadow: "none",
                }}
              >
                <CardContent>
                  <TableContainer sx={{ overflowX: "hidden" }}>
                    <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "25%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Location Name
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ width: "30%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Address
                            </MDTypography>
                          </TableCell>
                          <TableCell align="center" sx={{ width: "15%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Chargers
                            </MDTypography>
                          </TableCell>
                          <TableCell align="center" sx={{ width: "15%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Configured Timings
                            </MDTypography>
                          </TableCell>
                          <TableCell align="center" sx={{ width: "15%" }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={darkMode ? "white" : "dark"}
                            >
                              Action
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chargerLocations.map((location) => {
                          const totalTimings = location.chargers.reduce(
                            (sum, charger) =>
                              sum + (charger.operationalTimings?.length || 0),
                            0
                          );
                          return (
                            <TableRow key={location.locationId}>
                              <TableCell sx={{ width: "25%" }}>
                                <AutoTooltip>
                                  <MDTypography
                                    variant="button"
                                    color={darkMode ? "white" : "dark"}
                                    sx={{
                                      display: "block",
                                      width: "100%",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {location.locationName}
                                  </MDTypography>
                                </AutoTooltip>
                              </TableCell>

                              <TableCell sx={{ width: "30%" }}>
                                <MDTypography
                                  variant="caption"
                                  color={darkMode ? "white" : "dark"}
                                >
                                  {location.address || "N/A"}
                                </MDTypography>
                              </TableCell>
                              <TableCell align="center" sx={{ width: "15%" }}>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  color="info"
                                >
                                  {location.chargers.length}
                                </MDTypography>
                              </TableCell>
                              <TableCell align="center" sx={{ width: "15%" }}>
                                <MDTypography
                                  variant="caption"
                                  color={darkMode ? "white" : "dark"}
                                >
                                  {totalTimings} slot(s)
                                </MDTypography>
                              </TableCell>
                              <TableCell align="center" sx={{ width: "15%" }}>
                                <MDButton
                                  variant="gradient"
                                  color={sidenavColor}
                                  size="small"
                                  onClick={() =>
                                    handleChargerTimingsConfig(
                                      location.locationId
                                    )
                                  }
                                >
                                  Configure
                                </MDButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </MDBox>
        )}

        {tabValue === 3 && (
          <MDBox mt={2}>
            {recipientsLoading ? (
              <MDBox textAlign="center" py={3}>
                <Loader />
              </MDBox>
            ) : (
              <NotificationRecipientSettings
                recipients={recipients}
                onRefresh={fetchRecipients}
                onSave={handleAddRecipient}
              />
            )}
          </MDBox>
        )}

      </DialogContent>

      {/* Location Operational Hours Modal */}
      <LocationOperationalHoursModal
        open={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          setSelectedLocationId(null);
        }}
        locationId={selectedLocationId}
        onSave={handleLocationSave}
      />

      {/* Charger Operational Timings Modal */}
      <ChargerOperationalTimingsModal
        open={chargerTimingsModalOpen}
        onClose={() => {
          setChargerTimingsModalOpen(false);
          setSelectedLocationId(null);
        }}
        locationId={selectedLocationId}
        onSave={handleChargerTimingsSave}
      />
    </Dialog>
  );
};

export default SettingsModal;
